import React, { type ChangeEvent, useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService } from '../utils/api';
import { useUser } from '../contexts/UserContext';

interface Code {
  id: string;
  code: string;
  courseName: string;
  grade: string;
  courseTerm: string;
  email: string;
  studentName: string;
  mobile: string;
  usageDate: string;
  creationDate: string;
  isBurned: boolean;
}

interface GeneralCode {
  id: string;
  code: string;
  courseName: string;
  grade: string;
  courseTerm: string;
  creationDate: string;
}

interface FilterData {
  courseName: string;
  code: string;
  email: string;
  mobile: string;
  grade: string;
  courseTerm: string;
  usageDateStart: string;
  usageDateEnd: string;
  creationDateStart: string;
  creationDateEnd: string;
  isBurned: string;
  codeType: string;
  teacherId: string;
}

interface Grade {
  id: string;
  name: string;
  description: string;
  level: number;
  code: string;
  active: boolean;
}

interface Teacher {
  id: string;
  fullName: string;
}

interface SortConfig {
  key: keyof Code | keyof GeneralCode;
  direction: 'asc' | 'desc';
}

type ActiveTab = 'codes' | 'general-codes';

const Codes: React.FC = () => {
  const navigate = useNavigate();
  const { user, isTeacher, isModerator, getTeacherId } = useUser();
  const [activeTab, setActiveTab] = useState<ActiveTab>('codes');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [showUsageDatePicker, setShowUsageDatePicker] = useState(false);
  const [showCreationDatePicker, setShowCreationDatePicker] = useState(false);
  const usageDatePickerRef = useRef<HTMLDivElement | null>(null);
  const creationDatePickerRef = useRef<HTMLDivElement | null>(null);
  const [filters, setFilters] = useState<FilterData>({
    courseName: '',
    code: '',
    email: '',
    mobile: '',
    grade: '',
    courseTerm: '',
    usageDateStart: '',
    usageDateEnd: '',
    creationDateStart: '',
    creationDateEnd: '',
    isBurned: '',
    codeType: '',
    teacherId: '',
  });

  // Add new state for applied filters
  const [appliedFilters, setAppliedFilters] = useState<FilterData>({
    courseName: '',
    code: '',
    email: '',
    mobile: '',
    grade: '',
    courseTerm: '',
    usageDateStart: '',
    usageDateEnd: '',
    creationDateStart: '',
    creationDateEnd: '',
    isBurned: '',
    codeType: '',
    teacherId: '',
  });

  // Add state for API data
  const [codes, setCodes] = useState<Code[]>([]);
  const [generalCodes, setGeneralCodes] = useState<GeneralCode[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // State for delete confirmation dialog
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState<string | null>(null);
  
  // State for grades and teachers
  const [grades, setGrades] = useState<Grade[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);

  // Fetch grades
  const fetchGrades = async () => {
    setLoadingGrades(true);
    try {
      const response = await authFetch(apiUrl('Grade'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGrades(data.data);
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      setGrades([]);
    } finally {
      setLoadingGrades(false);
    }
  };

  // Fetch teachers
  const fetchTeachers = async () => {
    setLoadingTeachers(true);
    try {
      const response = await authFetch(apiUrl('Teacher'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTeachers(data.data.map((teacher: any) => ({
          id: teacher.id,
          fullName: teacher.fullName
        })));
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };
  
  // Fetch codes from API
  useEffect(() => {
    if (activeTab !== 'codes') return;
    
    const fetchCodes = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Build the URL with query parameters - set codeType=0 for regular codes
        let url = apiUrl(`CourseCode/GetAllCodes?pageNumber=${currentPage}&pageSize=${itemsPerPage}&codeType=0`);
        
        // Add teacher filtering for Teacher role
        const teacherId = isTeacher() ? getTeacherId() : null;
        if (teacherId) {
          url += `&teacherId=${teacherId}`;
        }
        
        // Add filter parameters if they exist
        if (appliedFilters.courseName) url += `&courseName=${encodeURIComponent(appliedFilters.courseName)}`;
        if (appliedFilters.code) url += `&code=${encodeURIComponent(appliedFilters.code)}`;
        if (appliedFilters.email) url += `&studentEmail=${encodeURIComponent(appliedFilters.email)}`;
        if (appliedFilters.mobile) url += `&studentPhone=${encodeURIComponent(appliedFilters.mobile)}`;
        if (appliedFilters.grade) url += `&grade=${encodeURIComponent(appliedFilters.grade)}`;
        if (appliedFilters.courseTerm) url += `&courseTerm=${encodeURIComponent(appliedFilters.courseTerm)}`;
        if (appliedFilters.isBurned) url += `&isBurned=${appliedFilters.isBurned}`;
        if (appliedFilters.usageDateStart) url += `&usageDateStart=${encodeURIComponent(appliedFilters.usageDateStart)}`;
        if (appliedFilters.usageDateEnd) url += `&usageDateEnd=${encodeURIComponent(appliedFilters.usageDateEnd)}`;
        if (appliedFilters.creationDateStart) url += `&creationDateStart=${encodeURIComponent(appliedFilters.creationDateStart)}`;
        if (appliedFilters.creationDateEnd) url += `&creationDateEnd=${encodeURIComponent(appliedFilters.creationDateEnd)}`;
        if (appliedFilters.teacherId) url += `&teacherId=${encodeURIComponent(appliedFilters.teacherId)}`;
        
        const response = await authFetch(url, {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch codes');
        }
        
        const data = await response.json();
        
        if (data.success) {
          // Map API response to our Code interface using the new response structure
          const formattedCodes: Code[] = data.data.data.map((code: any) => ({
            id: code.id,
            code: code.code,
            courseName: code.courseName,
            grade: code.grade,
            courseTerm: code.courseTerm || '',
            email: code.studentEmail || '',
            studentName: code.studentName || '',
            mobile: code.studentPhone || '',
            usageDate: code.usageDate ? new Date(code.usageDate).toISOString().split('T')[0] : '',
            creationDate: code.createdAt ? new Date(code.createdAt).toISOString().split('T')[0] : '',
            isBurned: code.isBurned || false
          }));
          
          setCodes(formattedCodes);
          setTotalItems(data.data.totalCount);
          setTotalPages(data.data.totalPages);
        } else {
          throw new Error(data.message || 'Failed to fetch codes');
        }
      } catch (error) {
        console.error('Error fetching codes:', error);
        setError('Failed to fetch codes. Please try again.');
        setCodes([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCodes();
  }, [currentPage, itemsPerPage, appliedFilters, activeTab, isTeacher, getTeacherId]);

  // Fetch grades and teachers on component mount
  useEffect(() => {
    fetchGrades();
    fetchTeachers();
  }, []);

  // Fetch general codes data from API
  useEffect(() => {
    if (activeTab !== 'general-codes') return;
    
    const fetchGeneralCodes = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Build the URL with query parameters - using the same API endpoint but with codeType=1 for GeneralCode
        let url = apiUrl(`CourseCode/GetAllCodes?codeType=1&pageNumber=${currentPage}&pageSize=${itemsPerPage}`);
        
        // Add teacher filtering for Teacher role
        const teacherId = isTeacher() ? getTeacherId() : null;
        if (teacherId) {
          url += `&teacherId=${teacherId}`;
        }
        
        // Add filter parameters if they exist - limiting to just what we need for general codes
        if (appliedFilters.courseName) url += `&courseName=${encodeURIComponent(appliedFilters.courseName)}`;
        if (appliedFilters.code) url += `&code=${encodeURIComponent(appliedFilters.code)}`;
        if (appliedFilters.grade) url += `&grade=${encodeURIComponent(appliedFilters.grade)}`;
        if (appliedFilters.courseTerm) url += `&courseTerm=${encodeURIComponent(appliedFilters.courseTerm)}`;
        if (appliedFilters.creationDateStart) url += `&creationDateStart=${encodeURIComponent(appliedFilters.creationDateStart)}`;
        if (appliedFilters.creationDateEnd) url += `&creationDateEnd=${encodeURIComponent(appliedFilters.creationDateEnd)}`;
        if (appliedFilters.teacherId) url += `&teacherId=${encodeURIComponent(appliedFilters.teacherId)}`;
        
        const response = await authFetch(url, {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch general codes');
        }
        
        const data = await response.json();
        
        if (data.success) {
          // Map API response to our GeneralCode interface using the new response structure
          const formattedGeneralCodes: GeneralCode[] = data.data.data.map((code: any) => ({
            id: code.id,
            code: code.code,
            courseName: code.courseName,
            grade: code.grade,
            courseTerm: code.courseTerm || '',
            creationDate: code.createdAt ? new Date(code.createdAt).toISOString().split('T')[0] : ''
          }));
          
          setGeneralCodes(formattedGeneralCodes);
          setTotalItems(data.data.totalCount);
          setTotalPages(data.data.totalPages);
        } else {
          throw new Error(data.message || 'Failed to fetch general codes');
        }
      } catch (error) {
        console.error('Error fetching general codes:', error);
        setError('Failed to fetch general codes. Please try again.');
        setGeneralCodes([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGeneralCodes();
  }, [currentPage, itemsPerPage, appliedFilters, activeTab, isTeacher, getTeacherId]);

  // Sorting logic
  const handleSort = (key: keyof Code | keyof GeneralCode) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting for codes
  const sortedCodes = useMemo(() => {
    let sortableCodes = [...codes];
    if (sortConfig !== null) {
      sortableCodes.sort((a, b) => {
        if (a[sortConfig.key as keyof Code] < b[sortConfig.key as keyof Code]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key as keyof Code] > b[sortConfig.key as keyof Code]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableCodes;
  }, [codes, sortConfig]);

  // Apply sorting for general codes
  const sortedGeneralCodes = useMemo(() => {
    let sortableGeneralCodes = [...generalCodes];
    if (sortConfig !== null) {
      sortableGeneralCodes.sort((a, b) => {
        if (a[sortConfig.key as keyof GeneralCode] < b[sortConfig.key as keyof GeneralCode]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key as keyof GeneralCode] > b[sortConfig.key as keyof GeneralCode]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableGeneralCodes;
  }, [generalCodes, sortConfig]);

  // Apply filters to applied filters state
  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Reset all filters
  const handleResetFilters = () => {
    const resetFilters = {
      courseName: '',
      code: '',
      email: '',
      mobile: '',
      grade: '',
      courseTerm: '',
      usageDateStart: '',
      usageDateEnd: '',
      creationDateStart: '',
      creationDateEnd: '',
      isBurned: '',
      codeType: '',
      teacherId: '',
    };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setCurrentPage(1); // Reset to first page when resetting
  };

  // We don't need filteredCodes logic anymore since we're filtering server-side
  // Just use the sortedCodes directly for the active tab

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Pagination will be handled by the API call in useEffect
  };

  // Navigate to code generation page
  const handleGenerateCode = () => {
    navigate('/codes/generate');
  };

  // Add state for general code modal
  const [showGeneralCodeModal, setShowGeneralCodeModal] = useState(false);
  const [generalCodeForm, setGeneralCodeForm] = useState({
    grade: '',
    courseTerm: '',
    courseName: '',
    code: ''
  });
  
  // New state for courses in the modal
  const [modalCourses, setModalCourses] = useState<Array<{id: string; courseName: string}>>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseError, setCourseError] = useState<string | null>(null);
  
  // Fetch courses for the modal
  const fetchCoursesForModal = async (grade: string, courseTerm: string) => {
    if (!grade || !courseTerm) return;
    
    setLoadingCourses(true);
    setCourseError(null);
    try {
      const response = await authFetch(
        apiUrl(`Course/bygradeandterm?grade=${grade}&term=${courseTerm}&pageNumber=1&pageSize=100`),
        {
          headers: {
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch courses');
      }
      
      const data = await response.json();
      if (data.success) {
        setModalCourses(data.data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch courses');
      }
    } catch (err) {
      setCourseError('Failed to load courses. Please try again.');
      setModalCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };
  
  // Watch for changes in grade and courseTerm to fetch courses
  useEffect(() => {
    if (showGeneralCodeModal && generalCodeForm.grade && generalCodeForm.courseTerm) {
      fetchCoursesForModal(generalCodeForm.grade, generalCodeForm.courseTerm);
    }
  }, [showGeneralCodeModal, generalCodeForm.grade, generalCodeForm.courseTerm]);
  
  // Handle general code modal open
  const handleOpenGeneralCodeModal = () => {
    setShowGeneralCodeModal(true);
    // Reset the form
    setGeneralCodeForm({
      grade: '',
      courseTerm: '',
      courseName: '',
      code: ''
    });
    // Reset courses
    setModalCourses([]);
  };
  
  // Handle general code modal close
  const handleCloseGeneralCodeModal = () => {
    setShowGeneralCodeModal(false);
    // Reset form
    setGeneralCodeForm({
      grade: '',
      courseTerm: '',
      courseName: '',
      code: ''
    });
    // Reset courses
    setModalCourses([]);
  };
  
  // Handle general code form changes
  const handleGeneralCodeFormChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // If grade or courseTerm changes, reset courseName
    if (name === 'grade' || name === 'courseTerm') {
      setGeneralCodeForm({
        ...generalCodeForm,
        [name]: value,
        courseName: ''
      });
    } else {
      setGeneralCodeForm({
        ...generalCodeForm,
        [name]: value
      });
    }
  };
  
  // Handle generate general code
  const handleGenerateGeneralCode = async () => {
    // Validate form data
    if (!generalCodeForm.grade || !generalCodeForm.courseTerm || !generalCodeForm.courseName || !generalCodeForm.code) {
      alert('Please fill out all fields');
      return;
    }
    
    try {
      // Make API call to generate general code
      const response = await authFetch(
        apiUrl(`CourseCode/GenerateGeneralCode?Courseid=${generalCodeForm.courseName}&Code=${generalCodeForm.code}`),
        {
          method: 'POST',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to generate general code');
      }
      
      const result = await response.json();
      
      if (result.success) {
        // Show success message
        alert(result.message || 'General code generated successfully!');
        // Close the modal
        handleCloseGeneralCodeModal();
        // Refresh the general codes list
        if (activeTab === 'general-codes') {
          window.location.reload(); // Simple refresh for now
        }
      } else {
        throw new Error(result.message || 'Failed to generate general code');
      }
    } catch (error) {
      console.error('Error generating general code:', error);
      alert('Failed to generate general code. Please try again.');
    }
  };

  // Handle showing delete confirmation
  const handleShowDeleteConfirmation = (id: string) => {
    setCodeToDelete(id);
    setShowDeleteConfirmation(true);
  };

  // Handle canceling delete
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setCodeToDelete(null);
  };

  // Handle delete general code
  const handleDeleteGeneralCode = async () => {
    if (!codeToDelete) return;
    
    try {
      // Make API call to delete the general code
      const response = await authFetch(
        apiUrl(`CourseCode/DeleteCode/${codeToDelete}`),
        {
          method: 'DELETE',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete general code');
      }

      const result = await response.json();

      if (result.success) {
        alert(result.message || 'General code deleted successfully!');
        
        // Close the confirmation dialog
        setShowDeleteConfirmation(false);
        setCodeToDelete(null);
        
        // Refresh the general codes list
        if (activeTab === 'general-codes') {
          window.location.reload(); // Simple refresh for now
        }
      } else {
        throw new Error(result.message || 'Failed to delete general code');
      }
    } catch (error) {
      console.error('Error deleting general code:', error);
      alert('Failed to delete general code. Please try again.');
      setShowDeleteConfirmation(false);
      setCodeToDelete(null);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (usageDatePickerRef.current && !usageDatePickerRef.current.contains(event.target as Node)) {
        setShowUsageDatePicker(false);
      }
      if (creationDatePickerRef.current && !creationDatePickerRef.current.contains(event.target as Node)) {
        setShowCreationDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatDateDisplay = (startDate: string, endDate: string) => {
    if (!startDate && !endDate) return '';
    if (startDate && !endDate) return new Date(startDate).toLocaleDateString();
    if (!startDate && endDate) return `إلى ${new Date(endDate).toLocaleDateString()}`;
    return `${new Date(startDate).toLocaleDateString()} إلى ${new Date(endDate).toLocaleDateString()}`;
  };

  const handleUsageDateSelect = (date: string) => {
    if (!filters.usageDateStart || (filters.usageDateStart && filters.usageDateEnd)) {
      // Start a new range
      setFilters({
        ...filters,
        usageDateStart: date,
        usageDateEnd: ''
      });
    } else {
      // Complete the range
      const startDate = new Date(filters.usageDateStart);
      const endDate = new Date(date);
      
      // If end date is before start date, swap them
      if (endDate < startDate) {
        setFilters({
          ...filters,
          usageDateStart: date,
          usageDateEnd: filters.usageDateStart
        });
      } else {
        setFilters({
          ...filters,
          usageDateEnd: date
        });
      }
    }
  };

  const handleCreationDateSelect = (date: string) => {
    if (!filters.creationDateStart || (filters.creationDateStart && filters.creationDateEnd)) {
      // Start a new range
      setFilters({
        ...filters,
        creationDateStart: date,
        creationDateEnd: ''
      });
    } else {
      // Complete the range
      const startDate = new Date(filters.creationDateStart);
      const endDate = new Date(date);
      
      // If end date is before start date, swap them
      if (endDate < startDate) {
        setFilters({
          ...filters,
          creationDateStart: date,
          creationDateEnd: filters.creationDateStart
        });
      } else {
        setFilters({
          ...filters,
          creationDateEnd: date
        });
      }
    }
  };

  const clearUsageDateRange = () => {
    setFilters({
      ...filters,
      usageDateStart: '',
      usageDateEnd: ''
    });
  };

  const clearCreationDateRange = () => {
    setFilters({
      ...filters,
      creationDateStart: '',
      creationDateEnd: ''
    });
  };

  const [usageDateMonth, setUsageDateMonth] = useState(new Date().getMonth());
  const [usageDateYear, setUsageDateYear] = useState(new Date().getFullYear());
  const [creationDateMonth, setCreationDateMonth] = useState(new Date().getMonth());
  const [creationDateYear, setCreationDateYear] = useState(new Date().getFullYear());

  const handleUsageDatePrevMonth = () => {
    if (usageDateMonth === 0) {
      setUsageDateMonth(11);
      setUsageDateYear(usageDateYear - 1);
    } else {
      setUsageDateMonth(usageDateMonth - 1);
    }
  };

  const handleUsageDateNextMonth = () => {
    if (usageDateMonth === 11) {
      setUsageDateMonth(0);
      setUsageDateYear(usageDateYear + 1);
    } else {
      setUsageDateMonth(usageDateMonth + 1);
    }
  };

  const handleCreationDatePrevMonth = () => {
    if (creationDateMonth === 0) {
      setCreationDateMonth(11);
      setCreationDateYear(creationDateYear - 1);
    } else {
      setCreationDateMonth(creationDateMonth - 1);
    }
  };

  const handleCreationDateNextMonth = () => {
    if (creationDateMonth === 11) {
      setCreationDateMonth(0);
      setCreationDateYear(creationDateYear + 1);
    } else {
      setCreationDateMonth(creationDateMonth + 1);
    }
  };

  const getMonthName = (month: number, year: number) => {
    return new Date(year, month, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month, 1).getDay();
  };

  return (
    <div className="p-6" dir="rtl">
      <h1 className="text-2xl font-bold mb-4">أكواد الباقات</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-reverse space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('codes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'codes'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            الأكواد
          </button>
          <button
            onClick={() => setActiveTab('general-codes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'general-codes'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            الأكواد العامة
          </button>
        </nav>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'codes' ? (
        // Codes content
        <>
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <h2 className="text-lg font-semibold mb-4">الفلاتر</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم الباقة</label>
                <input
                  type="text"
                  value={filters.courseName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, courseName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="تصفية حسب اسم الباقة"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الكود</label>
                <input
                  type="text"
                  value={filters.code}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, code: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="تصفية حسب الكود"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">البريد الإلكتروني</label>
                <input
                  type="text"
                  value={filters.email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="تصفية حسب البريد الإلكتروني"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
                <input
                  type="text"
                  value={filters.mobile}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, mobile: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="تصفية حسب الجوال"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الصف</label>
                <select
                  value={filters.grade}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, grade: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  disabled={loadingGrades}
                >
                  <option value="">
                    {loadingGrades ? 'جاري تحميل الصفوف...' : 'كل الصفوف'}
                  </option>
                  {grades.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الفصل</label>
                <select
                  value={filters.courseTerm}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, courseTerm: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">كل الفصول</option>
                  <option value="First">الفصل الأول</option>
                  <option value="Second">الفصل الثاني</option>
                </select>
              </div>
              
              {/* Teacher Filter - Only for Moderators */}
              {isModerator() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المدرس</label>
                  <select
                    value={filters.teacherId}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, teacherId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    disabled={loadingTeachers}
                  >
                    <option value="">
                      {loadingTeachers ? 'جاري تحميل المدرسين...' : 'كل المدرسين'}
                    </option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Teacher Info Display - Only for Teachers */}
              {isTeacher() && user && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المدرس</label>
                  <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                    {user.name}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الحالة</label>
                <select
                  value={filters.isBurned}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, isBurned: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">كل الحالات</option>
                  <option value="true">مستخدم</option>
                  <option value="false">غير مستخدم</option>
                </select>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">نطاق تاريخ الاستخدام</label>
                <div 
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary flex justify-between items-center cursor-pointer"
                  onClick={() => setShowUsageDatePicker(!showUsageDatePicker)}
                >
                  <span className={`${!filters.usageDateStart && !filters.usageDateEnd ? 'text-gray-400' : ''}`}>
                    {formatDateDisplay(filters.usageDateStart, filters.usageDateEnd) || 'اختر نطاق التاريخ...'}
                  </span>
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                </div>
                
                {showUsageDatePicker && (
                  <div 
                    ref={usageDatePickerRef}
                    className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4"
                    style={{ width: '300px' }}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-sm font-medium text-gray-700">اختر نطاق تاريخ الاستخدام</h3>
                      <button 
                        onClick={clearUsageDateRange}
                        className="text-xs text-gray-500 hover:text-gray-700"
                        type="button"
                      >
                        مسح
                      </button>
                    </div>
                    
                    <div className="mb-2 text-xs text-gray-500">
                      {filters.usageDateStart 
                        ? (filters.usageDateEnd 
                          ? 'تم اختيار نطاق التاريخ' 
                          : 'اختر تاريخ الانتهاء') 
                        : 'اختر تاريخ البدء'}
                    </div>
                    
                    {/* Calendar header */}
                    <div className="mb-2">
                      <div className="flex justify-between items-center mb-2">
                        <button 
                          type="button"
                          className="text-gray-600 hover:text-gray-800"
                          onClick={handleUsageDatePrevMonth}
                        >
                          &lt;
                        </button>
                        <div className="text-sm font-medium">{getMonthName(usageDateMonth, usageDateYear)}</div>
                        <button 
                          type="button"
                          className="text-gray-600 hover:text-gray-800"
                          onClick={handleUsageDateNextMonth}
                        >
                          &gt;
                        </button>
                      </div>
                      
                      {/* Calendar weekdays */}
                      <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500">
                        <div>Su</div>
                        <div>Mo</div>
                        <div>Tu</div>
                        <div>We</div>
                        <div>Th</div>
                        <div>Fr</div>
                        <div>Sa</div>
                      </div>
                    </div>
                    
                    {/* Calendar days */}
                    <div className="grid grid-cols-7 gap-1">
                      {/* Render empty cells for days before the 1st of the month */}
                      {Array.from({ length: getFirstDayOfMonth(usageDateMonth, usageDateYear) }, (_, i) => (
                        <div key={`empty-start-${i}`} className="p-1" />
                      ))}
                      
                      {/* Render the days of the month */}
                      {Array.from({ length: getDaysInMonth(usageDateMonth, usageDateYear) }, (_, i) => {
                        const day = i + 1;
                        const date = `${usageDateYear}-${(usageDateMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                        const isStartDate = filters.usageDateStart === date;
                        const isEndDate = filters.usageDateEnd === date;
                        const isInRange = filters.usageDateStart && filters.usageDateEnd && 
                                         new Date(date) >= new Date(filters.usageDateStart) && 
                                         new Date(date) <= new Date(filters.usageDateEnd);
                        
                        return (
                          <button
                            key={date}
                            type="button"
                            onClick={() => handleUsageDateSelect(date)}
                            className={`p-1 text-sm rounded-md ${
                              isStartDate || isEndDate
                                ? 'bg-primary text-white' 
                                : isInRange
                                ? 'bg-primary/20 text-primary'
                                : 'hover:bg-gray-100'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    
                    {filters.usageDateStart && filters.usageDateEnd && (
                      <div className="mt-3 text-xs text-gray-600">
                        Selected: {new Date(filters.usageDateStart).toLocaleDateString()} to {new Date(filters.usageDateEnd).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">نطاق تاريخ الإنشاء</label>
                <div 
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary flex justify-between items-center cursor-pointer"
                  onClick={() => setShowCreationDatePicker(!showCreationDatePicker)}
                >
                  <span className={`${!filters.creationDateStart && !filters.creationDateEnd ? 'text-gray-400' : ''}`}>
                    {formatDateDisplay(filters.creationDateStart, filters.creationDateEnd) || 'اختر نطاق التاريخ...'}
                  </span>
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                </div>
                
                {showCreationDatePicker && (
                  <div 
                    ref={creationDatePickerRef}
                    className="absolute z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4"
                    style={{ width: '300px' }}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-sm font-medium text-gray-700">اختر نطاق تاريخ الإنشاء</h3>
                      <button 
                        onClick={clearCreationDateRange}
                        className="text-xs text-gray-500 hover:text-gray-700"
                        type="button"
                      >
                        مسح
                      </button>
                    </div>
                    
                    <div className="mb-2 text-xs text-gray-500">
                      {filters.creationDateStart 
                        ? (filters.creationDateEnd 
                          ? 'تم اختيار نطاق التاريخ' 
                          : 'اختر تاريخ الانتهاء') 
                        : 'اختر تاريخ البدء'}
                    </div>
                    
                    {/* Calendar header */}
                    <div className="mb-2">
                      <div className="flex justify-between items-center mb-2">
                        <button 
                          type="button"
                          className="text-gray-600 hover:text-gray-800"
                          onClick={handleCreationDatePrevMonth}
                        >
                          &lt;
                        </button>
                        <div className="text-sm font-medium">{getMonthName(creationDateMonth, creationDateYear)}</div>
                        <button 
                          type="button"
                          className="text-gray-600 hover:text-gray-800"
                          onClick={handleCreationDateNextMonth}
                        >
                          &gt;
                        </button>
                      </div>
                      
                      {/* Calendar weekdays */}
                      <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500">
                        <div>Su</div>
                        <div>Mo</div>
                        <div>Tu</div>
                        <div>We</div>
                        <div>Th</div>
                        <div>Fr</div>
                        <div>Sa</div>
                      </div>
                    </div>
                    
                    {/* Calendar days */}
                    <div className="grid grid-cols-7 gap-1">
                      {/* Render empty cells for days before the 1st of the month */}
                      {Array.from({ length: getFirstDayOfMonth(creationDateMonth, creationDateYear) }, (_, i) => (
                        <div key={`empty-start-${i}`} className="p-1" />
                      ))}
                      
                      {/* Render the days of the month */}
                      {Array.from({ length: getDaysInMonth(creationDateMonth, creationDateYear) }, (_, i) => {
                        const day = i + 1;
                        const date = `${creationDateYear}-${(creationDateMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
                        const isStartDate = filters.creationDateStart === date;
                        const isEndDate = filters.creationDateEnd === date;
                        const isInRange = filters.creationDateStart && filters.creationDateEnd && 
                                         new Date(date) >= new Date(filters.creationDateStart) && 
                                         new Date(date) <= new Date(filters.creationDateEnd);
                        
                        return (
                          <button
                            key={date}
                            type="button"
                            onClick={() => handleCreationDateSelect(date)}
                            className={`p-1 text-sm rounded-md ${
                              isStartDate || isEndDate
                                ? 'bg-primary text-white' 
                                : isInRange
                                ? 'bg-primary/20 text-primary'
                                : 'hover:bg-gray-100'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    
                    {filters.creationDateStart && filters.creationDateEnd && (
                      <div className="mt-3 text-xs text-gray-600">
                        Selected: {new Date(filters.creationDateStart).toLocaleDateString()} to {new Date(filters.creationDateEnd).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-start space-x-reverse space-x-4">
              <button
                onClick={handleSearch}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
              >
                بحث
              </button>
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إعادة تعيين
              </button>
            </div>
          </div>

          {/* Codes Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <span className="font-medium">المجموع: {totalItems}</span>
              <div className="flex gap-2">
                <button
                  onClick={handleGenerateCode}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  إنشاء كود
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      'code',
                      'courseName',
                      'grade',
                      'courseTerm',
                      'email',
                      'studentName',
                      'mobile',
                      'usageDate',
                      'creationDate',
                      'isBurned',
                    ].map((key) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key as keyof Code)}
                        className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      >
                        {key === 'code' ? 'الكود' :
                         key === 'courseName' ? 'اسم الباقة' : 
                         key === 'grade' ? 'الصف' :
                         key === 'courseTerm' ? 'الفصل' :
                         key === 'email' ? 'البريد الإلكتروني' :
                         key === 'studentName' ? 'اسم الطالب' :
                         key === 'mobile' ? 'رقم الهاتف' :
                         key === 'usageDate' ? 'تاريخ الاستخدام' :
                         key === 'creationDate' ? 'تاريخ الإنشاء' :
                         key === 'isBurned' ? 'الحالة' :
                         key.charAt(0).toUpperCase() + key.slice(1)}
                        {sortConfig?.key === key && (
                          <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="px-6 py-12 text-center">
                        <div className="flex justify-center">
                          <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={10} className="px-6 py-4 text-center text-red-500">
                        {error}
                      </td>
                    </tr>
                  ) : sortedCodes.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                        لا توجد أكواد مطابقة للمعايير
                      </td>
                    </tr>
                  ) : (
                    sortedCodes.map((code) => (
                    <tr key={code.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap font-medium">{code.code}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.courseName}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.grade}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.courseTerm}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.studentName}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.mobile}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.usageDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.creationDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          code.isBurned 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {code.isBurned ? 'مستخدم' : 'غير مستخدم'}
                        </span>
                      </td>
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Enhanced Pagination */}
            {totalItems > 0 && !loading && !error && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="hidden sm:block">
                  <p className="text-sm text-gray-700">
                    عرض <span className="font-medium">{currentPage * itemsPerPage - itemsPerPage + 1}</span> إلى{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, totalItems)}
                    </span>{' '}
                    من <span className="font-medium">{totalItems}</span> نتائج
                  </p>
                </div>
                <div className="flex justify-center sm:justify-end mt-2 sm:mt-0">
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">الأول</span>
                      <span>&laquo;</span>
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">السابق</span>
                      <span>&lsaquo;</span>
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => 
                        page === 1 || 
                        page === totalPages || 
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      )
                      .map((page, index, array) => {
                        const prevPage = array[index - 1];
                        // Add ellipsis if there's a gap
                        if (index > 0 && prevPage !== page - 1) {
                          return [
                            <span key={`ellipsis-${prevPage}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>,
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                                currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          ];
                        }
                        
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                              currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      }).flat()}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">التالي</span>
                      <span>&rsaquo;</span>
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">الأخير</span>
                      <span>&raquo;</span>
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        // General Codes content
        <>
          {/* Filters for General Codes */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <h2 className="text-lg font-semibold mb-4">الفلاتر</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم الباقة</label>
                <input
                  type="text"
                  value={filters.courseName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, courseName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="تصفية حسب اسم الباقة"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الكود</label>
                <input
                  type="text"
                  value={filters.code}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, code: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="تصفية حسب الكود"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الصف</label>
                <select
                  value={filters.grade}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, grade: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  disabled={loadingGrades}
                >
                  <option value="">
                    {loadingGrades ? 'جاري تحميل الصفوف...' : 'كل الصفوف'}
                  </option>
                  {grades.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الفصل</label>
                <select
                  value={filters.courseTerm}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, courseTerm: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">كل الفصول</option>
                  <option value="First">الفصل الأول</option>
                  <option value="Second">الفصل الثاني</option>
                </select>
              </div>
              
              {/* Teacher Filter - Only for Moderators */}
              {isModerator() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المدرس</label>
                  <select
                    value={filters.teacherId}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, teacherId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    disabled={loadingTeachers}
                  >
                    <option value="">
                      {loadingTeachers ? 'جاري تحميل المدرسين...' : 'كل المدرسين'}
                    </option>
                    {teachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.fullName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Teacher Info Display - Only for Teachers */}
              {isTeacher() && user && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">المدرس</label>
                  <div className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700">
                    {user.name}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-start space-x-reverse space-x-4">
              <button
                onClick={handleSearch}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
              >
                بحث
              </button>
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إعادة تعيين
              </button>
            </div>
          </div>

          {/* General Codes Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <span className="font-medium">المجموع: {totalItems}</span>
              <button
                onClick={handleOpenGeneralCodeModal}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                إنشاء كود عام
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      'code',
                      'courseName',
                      'grade',
                      'courseTerm',
                      'creationDate',
                      'options',
                    ].map((key) => (
                      <th
                        key={key}
                        onClick={() => key !== 'options' && handleSort(key as keyof GeneralCode)}
                        className={`px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider ${key !== 'options' ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                      >
                        {key === 'code' ? 'الكود' :
                         key === 'courseName' ? 'اسم الباقة' : 
                         key === 'grade' ? 'الصف' :
                         key === 'courseTerm' ? 'الفصل' :
                         key === 'creationDate' ? 'تاريخ الإنشاء' :
                         key === 'options' ? 'خيارات' :
                         key.charAt(0).toUpperCase() + key.slice(1)}
                        {sortConfig?.key === key && (
                          <span>{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center">
                        <div className="flex justify-center">
                          <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-red-500">
                        {error}
                      </td>
                    </tr>
                  ) : sortedGeneralCodes.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                        لا توجد أكواد عامة
                      </td>
                    </tr>
                  ) : (
                    sortedGeneralCodes.map((code) => (
                    <tr key={code.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap font-medium">{code.code}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.courseName}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.grade}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.courseTerm}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{code.creationDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-reverse space-x-2">
                          <button 
                            onClick={() => handleShowDeleteConfirmation(code.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination for general codes */}
            {totalItems > 0 && !loading && !error && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="hidden sm:block">
                  <p className="text-sm text-gray-700">
                    عرض <span className="font-medium">{currentPage * itemsPerPage - itemsPerPage + 1}</span> إلى{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, totalItems)}
                    </span>{' '}
                    من <span className="font-medium">{totalItems}</span> نتائج
                  </p>
                </div>
                <div className="flex justify-center sm:justify-end mt-2 sm:mt-0">
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">الأول</span>
                      <span>&laquo;</span>
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">السابق</span>
                      <span>&lsaquo;</span>
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => 
                        page === 1 || 
                        page === totalPages || 
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      )
                      .map((page, index, array) => {
                        const prevPage = array[index - 1];
                        // Add ellipsis if there's a gap
                        if (index > 0 && prevPage !== page - 1) {
                          return [
                            <span key={`ellipsis-${prevPage}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>,
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                                currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          ];
                        }
                        
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                              currentPage === page ? 'z-10 bg-primary text-white border-primary' : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      }).flat()}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">التالي</span>
                      <span>&rsaquo;</span>
                    </button>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="sr-only">الأخير</span>
                      <span>&raquo;</span>
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      
      {/* General Code Modal */}
      {showGeneralCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">توليد كود عام</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الصف</label>
                <select
                  name="grade"
                  value={generalCodeForm.grade}
                  onChange={handleGeneralCodeFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">اختر الصف</option>
                  <option value="Secondary1">الصف الأول الثانوي</option>
                  <option value="Secondary2">الصف الثاني الثانوي</option>
                  <option value="Secondary3">الصف الثالث الثانوي</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الفصل</label>
                <select
                  name="courseTerm"
                  value={generalCodeForm.courseTerm}
                  onChange={handleGeneralCodeFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">اختر الفصل</option>
                  <option value="First">الفصل الأول</option>
                  <option value="Second">الفصل الثاني</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الباقة</label>
                <select
                  name="courseName"
                  value={generalCodeForm.courseName}
                  onChange={handleGeneralCodeFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  disabled={!generalCodeForm.grade || !generalCodeForm.courseTerm || loadingCourses}
                >
                  <option value="">
                    {loadingCourses 
                      ? 'جاري تحميل الباقات...' 
                      : courseError 
                      ? 'خطأ في تحميل الباقات' 
                      : 'اختر الباقة'
                    }
                  </option>
                  {modalCourses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.courseName}
                    </option>
                  ))}
                </select>
                {courseError && (
                  <p className="mt-1 text-sm text-red-600">{courseError}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الكود</label>
                <input
                  type="text"
                  name="code"
                  value={generalCodeForm.code}
                  onChange={handleGeneralCodeFormChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="أدخل الكود"
                />
              </div>
            </div>
            
            <div className="mt-6 flex justify-start gap-2">
              <button
                onClick={handleCloseGeneralCodeModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleGenerateGeneralCode}
                className={`bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors ${
                  loadingCourses ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={loadingCourses}
              >
                إنشاء
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">حذف الكود العام</h2>
            <p>هل أنت متأكد أنك تريد حذف هذا الكود العام؟</p>
            <div className="mt-6 flex justify-start gap-2">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleDeleteGeneralCode}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Codes; 